import { useMutation, useQueryClient } from "@tanstack/react-query";

import { workbenchAnalystNotesApi } from "../../../api/client";
import { useForceExitStore } from "../../../stores/forceExitStore";
import { analystNotesKeys } from "./analystNotesKeys";

/**
 * Mutation hook for saving a new analyst note against a deal. Backed by
 * `WorkbenchAnalystNotesApi.saveAnalystNote` (POST), which forwards the note to
 * recap-service; recap derives the author + timestamp server-side and returns
 * 204 No Content. A per-attempt `Idempotency-Key` dedups accidental
 * double-submits. On success the deal's notes list is invalidated so the table
 * refetches and shows the new note.
 * @param {string} approvalId - The numeric approval / vehicle-loan-application id.
 * @returns {ReturnType<typeof useMutation>} The TanStack Mutation result.
 */
export function useSaveAnalystNote(approvalId: string) {
  const queryClient = useQueryClient();
  const shouldForceExit = useForceExitStore((state) => state.shouldForceExit);

  return useMutation({
    mutationFn: async (note: string): Promise<void> => {
      if (shouldForceExit) {
        return;
      }
      const idempotencyKey = globalThis.crypto?.randomUUID?.();
      await workbenchAnalystNotesApi.saveAnalystNote(approvalId, { note }, idempotencyKey);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: analystNotesKeys.list({ approvalId }) });
    },
  });
}
